import { useQueries } from "@tanstack/react-query";
import { metricsApi } from "../api/metrics";
import { qk } from "../../lib/queryKeys";
import type { MetricsFilters } from "../api/types";
import {
  toComparisonKey,
  getMetricRagStatus,
  type RagColor,
} from "../utils";

/** Demographic dimensions a metric can be broken down by (region not yet supported by the API). */
export const BREAKDOWN_DIMENSIONS = [
  { groupBy: "department", label: "By department" },
  { groupBy: "age_group", label: "By age" },
  { groupBy: "gender", label: "By gender" },
  { groupBy: "shift_pattern", label: "By shift pattern" },
] as const;

export type BreakdownGroupByValue = (typeof BREAKDOWN_DIMENSIONS)[number]["groupBy"];

export interface BreakdownRow {
  label: string;
  value: number;
  ragColor?: RagColor;
  ragLabel?: string;
}

export interface MetricBreakdownDimension {
  groupBy: BreakdownGroupByValue;
  label: string;
  rows: BreakdownRow[];
  isLoading: boolean;
  isError: boolean;
}

export interface UseMetricBreakdownsResult {
  /** True when this metric maps to a comparison-API-supported key. */
  supported: boolean;
  dimensions: MetricBreakdownDimension[];
  isLoading: boolean;
}

/**
 * Fetches a metric's value broken down by each demographic dimension
 * (department / age / gender / shift), in parallel, and attaches RAG status to
 * each group using the UI metric's bands. Powers the inline metric drill-down
 * panels. Returns `supported: false` for metrics the comparison API can't break
 * down (e.g. steps, workout time) so callers can hide the affordance.
 *
 * `enabled` lets callers defer the requests until the panel is actually opened.
 */
export const useMetricBreakdowns = (
  uiMetricId: string,
  filterParams: MetricsFilters,
  options?: { enabled?: boolean }
): UseMetricBreakdownsResult => {
  const comparisonKey = toComparisonKey(uiMetricId);
  const supported = comparisonKey !== null;
  const enabled = (options?.enabled ?? true) && supported;

  const results = useQueries({
    queries: BREAKDOWN_DIMENSIONS.map((dim) => {
      const params = {
        ...filterParams,
        group_by: dim.groupBy,
        metrics: comparisonKey ? [comparisonKey] : [],
      };
      return {
        queryKey: qk.metrics.comparison(params),
        queryFn: () => metricsApi.getComparison(params),
        staleTime: 5 * 60 * 1000,
        enabled,
      };
    }),
  });

  const dimensions: MetricBreakdownDimension[] = BREAKDOWN_DIMENSIONS.map(
    (dim, index) => {
      const result = results[index];
      const groups = result?.data?.data?.groups ?? [];
      const rows: BreakdownRow[] = groups.flatMap((group) => {
        const raw = comparisonKey ? group.values[comparisonKey] : null;
        if (typeof raw !== "number") return [];
        const rounded = Math.round(raw * 10) / 10;
        // RAG bands live on the UI metric, not the comparison key.
        const status = getMetricRagStatus(uiMetricId, rounded);
        return [
          {
            label: group.group_label,
            value: rounded,
            ragColor: status.ragColor,
            ragLabel: status.label,
          },
        ];
      });

      return {
        groupBy: dim.groupBy,
        label: dim.label,
        rows,
        isLoading: result?.isLoading ?? false,
        isError: result?.isError ?? false,
      };
    }
  );

  return {
    supported,
    dimensions,
    isLoading: enabled && results.some((r) => r.isLoading),
  };
};
